import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import {
  datePaginatedTransactionsChannelName,
  operationsLogsChannelName,
  pendingTransactionsChannelName,
  rewardCardAccountsChannelName
} from '@/channels/utils/channelNames';
import useAPI from '@/hooks/useAPI';
import { useChannelData } from '@/hooks/useChannel';
import { RewardCardsParams } from '@/pages/RewardCardList/types';
import { DatePaginatedTransactionsParams } from '@/pages/TransactionList/components/DatePaginatedTransactionsList/types';
import { PendingTransactionsParams } from '@/pages/TransactionList/components/PendingTransactionsList/types';
import RewardCardRepository from '@/repositories/RewardCardRepository';
import PaginatedResponse, { DatePaginatedResponse } from '@/types/paginatedResponse';
import { QueryParams } from '@/types/query';
import { OperationsLog, RewardCardAccount, Transaction } from '@/types/rewardCard';

export const useRewardCardAccountsForPerson = (personId: string, params: RewardCardsParams) => {
  const queryClient = useQueryClient();
  const rewardCardRepository = RewardCardRepository();
  const queryKey = useMemo(
    () => ['getRewardCardAccountsForPerson', personId, params],
    [params, personId]
  );

  // Websocket channel updates
  const { channelData } = useChannelData<PaginatedResponse<RewardCardAccount[]>>({
    channelId: rewardCardAccountsChannelName(personId),
    channelName: 'reward card accounts list'
  });

  useEffect(() => {
    if (channelData) {
      queryClient.setQueryData<PaginatedResponse<RewardCardAccount[]>>(queryKey, channelData);
    }
  }, [channelData, queryClient, queryKey]);

  return useAPI({
    queryKey,
    queryFn: async () => {
      return await rewardCardRepository.getAccountsForPerson(personId, params);
    }
  });
};

export const useRewardCardAccount = (accountId: string) => {
  const rewardCardRepository = RewardCardRepository();

  return useAPI({
    queryKey: ['getRewardCardAccount', accountId],
    queryFn: async () => {
      return await rewardCardRepository.getAccount(accountId);
    }
  });
};

export const useRewardCardAccountPreflightValidation = (personId: string, enabled = true) => {
  const rewardCardRepository = RewardCardRepository();

  return useAPI({
    queryKey: ['createAccountPreflightValidation', personId],
    queryFn: async () => {
      return await rewardCardRepository.createAccountPreflightValidation(personId);
    },
    enabled,
    staleTime: 0
  });
};

export const usePendingTransactions = (
  personId: string,
  searchParams: PendingTransactionsParams,
  enabled = true
) => {
  const queryClient = useQueryClient();
  const rewardCardRepository = RewardCardRepository();
  const queryKey = useMemo(
    () => ['getPendingTransactionsForPerson', personId, searchParams],
    [personId, searchParams]
  );

  // Websocket channel updates
  const { channelData } = useChannelData<PaginatedResponse<Transaction[]>>({
    channelId: pendingTransactionsChannelName(personId),
    channelName: 'pending transactions list'
  });

  useEffect(() => {
    if (channelData) {
      queryClient.setQueryData<PaginatedResponse<Transaction[]>>(queryKey, channelData);
    }
  }, [channelData, queryClient, queryKey]);

  return useAPI({
    queryKey,
    queryFn: async () => {
      return await rewardCardRepository.getPendingTransactionsForPerson(personId, searchParams);
    },
    enabled
  });
};

export const useDatePaginatedTransactions = (
  personId: string,
  searchParams: DatePaginatedTransactionsParams,
  enabled = true
) => {
  const queryClient = useQueryClient();
  const rewardCardRepository = RewardCardRepository();
  const queryKey = useMemo(
    () => ['getDatePaginatedTransactionsForPerson', personId, searchParams],
    [personId, searchParams]
  );

  // Websocket channel updates
  const { channelData } = useChannelData<DatePaginatedResponse<Transaction[]>>({
    channelId: datePaginatedTransactionsChannelName(personId),
    channelName: 'transactions list'
  });

  useEffect(() => {
    if (channelData) {
      queryClient.setQueryData<PaginatedResponse<Transaction[]>>(queryKey, channelData);
    }
  }, [channelData, queryClient, queryKey]);

  return useAPI({
    queryKey,
    queryFn: async () => {
      return await rewardCardRepository.getDatePaginatedTransactionsForPerson(
        personId,
        searchParams
      );
    },
    enabled
  });
};

export const useOperationsLogs = ({
  personId,
  params,
  enabled = true
}: {
  personId: string;
  params?: QueryParams;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();
  const rewardCardRepository = RewardCardRepository();
  const queryKey = useMemo(() => ['getOperationsLogs', personId, params], [params, personId]);

  // Websocket channel updates
  const { channelData } = useChannelData<PaginatedResponse<OperationsLog[]>>({
    channelId: operationsLogsChannelName(personId),
    channelName: 'operations logs list',
    shouldDetach: false
  });

  useEffect(() => {
    if (channelData) {
      queryClient.setQueryData<PaginatedResponse<OperationsLog[]>>(queryKey, channelData);
    }
  }, [channelData, queryClient, queryKey]);

  return useAPI({
    queryKey,
    queryFn: async () => {
      return await rewardCardRepository.getOperationsLogs({ personId, params });
    },
    enabled
  });
};
